import React, { useEffect, useState } from 'react';
import { Download, Eye, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { getApiBase } from '../lib/apiBase';
import { usePilotContext } from '../hooks/usePilotContext';

export function ReportsPage() {
  const { context } = usePilotContext();
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchHtml = async () => {
    if (!context.customer_id) return;
    setLoading(true);
    try {
      const base = getApiBase();
      const token = localStorage.getItem('aerta_token');
      const res = await fetch(`${base}/api/reports/${context.customer_id}/html`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      setHtml(await res.text());
    } catch (e) {
      console.error('Report load failed:', e);
      setHtml('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHtml();
  }, [context.customer_id]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('aerta_token');
      const base = getApiBase();
      const res = await fetch(`${base}/api/reports/${context.customer_id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AERTA-Security-Assessment-${context.customer_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    }
  };

  const saveHtml = async () => {
    try {
      await api.post(`/api/reports/${context.customer_id}/html/save`, {});
      alert('HTML report saved on the server for your tenant.');
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground">Security assessment HTML/PDF export for pilot evidence pack.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={saveHtml}><Save className="mr-2 h-4 w-4" />Save HTML</Button>
          <Button variant="outline" onClick={downloadPdf}><Download className="mr-2 h-4 w-4" />Download PDF</Button>
          <Button onClick={fetchHtml} disabled={loading}><Eye className="mr-2 h-4 w-4" />Refresh HTML</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>HTML Preview</CardTitle>
          <CardDescription>Rendered directly from `/api/reports/{'{customer_id}'}/html`.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" /> : null}
          {!loading && !html ? <p className="text-sm text-muted-foreground">No report yet. Run risk scan and generate playbooks first.</p> : null}
          {html ? <div className="max-h-[70vh] overflow-auto rounded-md border border-border bg-secondary/20 p-3" dangerouslySetInnerHTML={{ __html: html }} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
